import React from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Mail, Instagram, Twitter, Youtube, Users, ArrowRight } from "lucide-react";
import { useTeam } from "@/hooks/useTeam";
import ShareButtons from "@/components/ShareButtons";
import SEO from "@/components/SEO";
import TikTok from "@/components/icons/TikTok";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card from "@/components/ui/Card";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import EmptyState from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";

// El navbar es fijo (h-16 en móvil, h-20 en escritorio) y ahora, encima,
// también la cinta promocional (~37px), que vive en el mismo bloque fijo que
// <Navbar>. Esta página no tenía ningún relleno superior propio, así que la
// barra de migas quedaba tapada debajo.
const CLEARANCE = "pt-28 md:pt-32";

const EquipoMiembro = () => {
  const { slug } = useParams();
  const { getMiembroBySlug, miembros, loading } = useTeam({ soloVisibles: true });
  const miembro = getMiembroBySlug(slug);

  // Antes se comprobaba solo `if (!miembro)`, sin mirar `loading`: en cada
  // primera carga la página mostraba «Perfil no encontrado» durante un
  // instante, antes incluso de que la petición hubiera respondido.
  if (loading) {
    return (
      <div className={`min-h-screen ${CLEARANCE}`}>
        <Section tone="light" width="reading" space="tight">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <Skeleton className="aspect-square rounded-2xl md:col-span-1" />
            <div className="md:col-span-2 space-y-4">
              <Skeleton className="h-10 w-2/3" />
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-4 w-full mt-8" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>
        </Section>
      </div>
    );
  }

  if (!miembro) {
    return (
      <div className={`min-h-screen ${CLEARANCE}`}>
        <SEO title="Perfil no encontrado" noIndex />
        <EmptyState
          icon={Users}
          title="Perfil no encontrado"
          description="Este perfil no existe o no está disponible en este momento."
          action={
            <CTAButton to="/quienes-somos" className="font-semibold">
              <ChevronLeft size={18} aria-hidden="true" /> Ver el equipo completo
            </CTAButton>
          }
          className="py-32"
        />
      </div>
    );
  }

  const otrosMiembros = miembros.filter(m => m.id !== miembro.id).slice(0, 3);
  const bio = (miembro.bio || miembro.descripcion || "").replace(/<[^>]+>/g, "");

  const redes = [
    miembro.redes?.instagram && { href: miembro.redes.instagram, icon: Instagram, label: "Instagram" },
    miembro.redes?.twitter && { href: miembro.redes.twitter, icon: Twitter, label: "Twitter / X" },
    miembro.redes?.youtube && { href: miembro.redes.youtube, icon: Youtube, label: "YouTube" },
    miembro.redes?.tiktok && { href: miembro.redes.tiktok, icon: TikTok, label: "TikTok" },
  ].filter(Boolean);

  return (
    <div className={`min-h-screen ${CLEARANCE}`}>
      <SEO
        title={`${miembro.nombre} — ${miembro.cargo}`}
        description={bio.slice(0, 160) || `${miembro.nombre}, ${miembro.cargo} en la Delegación de Evangelización Digital de la Diócesis de Huelva.`}
        image={miembro.foto || undefined}
        type="profile"
      />

      {/* Migas de pan */}
      <nav aria-label="Migas de pan" className="section-bg-muted border-b border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors duration-fast focus-ring rounded">Inicio</Link>
          <span aria-hidden="true">/</span>
          <Link to="/quienes-somos" className="hover:text-primary transition-colors duration-fast focus-ring rounded">
            Quiénes somos
          </Link>
          <span aria-hidden="true">/</span>
          <span className="text-foreground font-medium truncate" aria-current="page">{miembro.nombre}</span>
        </div>
      </nav>

      {/* Perfil */}
      <Section tone="light" width="reading" space="tight">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Columna izquierda */}
          <Reveal className="md:col-span-1" distance={24}>
            <div className="rounded-2xl overflow-hidden bg-muted aspect-square mb-5 shadow-rest">
              {miembro.foto ? (
                <img
                  src={miembro.foto}
                  alt={`Foto de ${miembro.nombre}`}
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                  decoding="async"
                  onError={e => { e.target.onerror = null; e.target.src = "/logos/logo-icono.png"; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-7xl font-bold text-primary/20 select-none" aria-hidden="true">
                    {miembro.nombre.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            {miembro.area && (
              <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-4">
                {miembro.area}
              </span>
            )}
            <div className="space-y-3">
              {miembro.email && (
                <a
                  href={`mailto:${miembro.email}`}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-fast focus-ring rounded"
                >
                  <Mail size={16} className="text-primary flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">{miembro.email}</span>
                </a>
              )}
              {redes.map(red => (
                <a
                  key={red.label}
                  href={red.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-fast focus-ring rounded"
                >
                  <red.icon size={16} className="text-primary flex-shrink-0" aria-hidden="true" />
                  {red.label}
                </a>
              ))}
            </div>
            <div className="mt-8">
              <ArrowLink to="/quienes-somos" className="text-muted-foreground hover:text-primary">
                <ChevronLeft size={16} aria-hidden="true" /> Ver todo el equipo
              </ArrowLink>
            </div>
          </Reveal>

          {/* Columna derecha */}
          <Reveal className="md:col-span-2" delay={80} distance={24}>
            <h1
              className="font-heading font-bold leading-tight text-balance"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-0.03em" }}
            >
              {miembro.nombre}
            </h1>
            <p className="text-lg font-semibold text-primary mt-2 mb-6">{miembro.cargo}</p>
            {bio && <p className="text-foreground/80 leading-relaxed">{bio}</p>}
            <div className="mt-8 pt-6 border-t border-border">
              <ShareButtons
                url={`/quienes-somos/equipo/${miembro.slug}`}
                title={`${miembro.nombre} — ${miembro.cargo}`}
                description={bio.slice(0, 160)}
                hashtags="EvangelizaciónDigital,Huelva"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Otros miembros */}
      {otrosMiembros.length > 0 && (
        <Section tone="muted" width="reading" space="tight" className="border-t border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-heading font-bold">Conoce también a…</h2>
            <ArrowLink to="/quienes-somos">
              Ver todos <ArrowRight size={16} aria-hidden="true" />
            </ArrowLink>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otrosMiembros.map((m, i) => (
              <Reveal key={m.id} delay={i * 70} distance={20}>
                <Card to={`/quienes-somos/equipo/${m.slug}`} className="flex items-center gap-3 p-4 rounded-xl h-full">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                    {m.foto ? (
                      <img
                        src={m.foto}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={e => { e.target.onerror = null; e.target.src = "/logos/logo-icono.png"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-primary/40 font-bold" aria-hidden="true">
                        {m.nombre.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{m.nombre}</p>
                    <p className="text-xs text-primary truncate">{m.cargo}</p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default EquipoMiembro;
